import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Chip,
  FormHelperTextProps,
  MenuItem,
  TextField,
  useAutocomplete,
} from "@mui/material";
import { DEFAULT_ALGOLIA_QUERY, EMPTY_ALGOLIA_QUERY } from "core/consts";
import { getKey } from "core/model/utils/strings";
import { AlgoliaAnalyticsName, Validation } from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import { SelectOption } from "ds_legacy/components/SelectInput";
import { getHelperText } from "ds_legacy/components/Validation";
import { Z_INDEX_DRAWER_CONTENT, margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { useAlgoliaSearchClient } from "dsl/hooks/useAlgoliaSearchClient";
import { debounce } from "lodash";
import { ListPlusIcon } from "lucide-react";
import React, {
  CSSProperties,
  ChangeEventHandler,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormElement, FormElementProps, isValid } from "react-forms-state";
import { AutocompleteProvided, Hit } from "react-instantsearch-core";
import {
  Configure,
  InstantSearch,
  connectAutoComplete,
} from "react-instantsearch-dom";
import { useTranslations } from "translations";
import { useMedia } from "../ResponsiveMedia";

export type AlgoliaSelectPresenterProps = {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  currentRefinement: string;
  customNoResultMessage?: React.ReactNode | null;
  elementName?: string;
  enableAutoHighlight?: boolean;
  hasCustomValidation?: boolean;
  hasErrorOverride?: boolean;
  hideError?: boolean;
  hits: Hit<any>[];
  id?: string;
  isDisabled?: boolean;
  isMultiple?: boolean;
  isRequired?: boolean;
  label?: string;
  ListboxProps?: ReturnType<
    ReturnType<typeof useAutocomplete>["getListboxProps"]
  >;
  maxOptions?: number;
  minCharacterForRefinement?: number;
  noOptionsText?: ReactNode;
  onChange: (arg: any) => void;
  pasteContext?: { buttonText: string; tooltipText: string };
  placeholder?: string;
  refine: (arg: any) => any;
  renderHits: (hit: Hit<any>, idx: number) => any;
  shouldCloseMenuOnSelect?: boolean;
  shouldDisableLabelShrink?: boolean;
  shouldDisableUnderline?: boolean;
  shouldFilterSelectedOptions?: boolean;
  shouldTruncateMenuItems?: boolean;
  withShortValue?: boolean;
  validation?: Validation;
  value?: SelectOption | SelectOption[] | null;
  withPaste?: boolean;
  withPortal?: boolean;
  zIndex?: CSSProperties["zIndex"];
};

const AlgoliaSelectConnectedWithForm = FormElement()<
  FormElementProps &
    Omit<
      AlgoliaSelectPresenterProps,
      "currentRefinement" | "hits" | "onChange" | "refine"
    > & {
      defaultRefinement: string;
    }
>((props) => (
  <AlgoliaSelect
    {...props}
    defaultRefinement={props.value?.label || props.defaultRefinement}
  />
));

export const usePaste = (
  onChange: (normalisedPaste: { label: string; value: string }[]) => void,
) => {
  const [didPaste, setDidPaste] = useState(false);
  useEffect(() => {
    if (didPaste) {
      navigator.clipboard
        .readText()
        .then((clipText: string) => {
          const normalisedPaste = clipText
            .split(",")
            .map((zip: string) => ({ label: zip, value: zip }));

          onChange(normalisedPaste);

          setDidPaste(false);
        })
        .catch((err) => {
          console.error("[usePaste]", err);
        });
    }
  }, [didPaste]);

  return () => setDidPaste(true);
};

const AlgoliaSelectPresenter = ({
  ariaLabel,
  ariaLabelledBy,
  currentRefinement,
  elementName,
  enableAutoHighlight,
  hasCustomValidation,
  hasErrorOverride,
  hideError,
  hits,
  id,
  isDisabled = false,
  isMultiple,
  isRequired,
  label,
  ListboxProps,
  maxOptions,
  minCharacterForRefinement = 0,
  noOptionsText,
  onChange,
  pasteContext,
  placeholder,
  refine,
  renderHits,
  shouldCloseMenuOnSelect = true,
  shouldDisableLabelShrink,
  shouldDisableUnderline,
  shouldFilterSelectedOptions,
  shouldTruncateMenuItems,
  validation,
  value,
  withPaste,
  withPortal,
  withShortValue = false,
  zIndex,
}: AlgoliaSelectPresenterProps) => {
  const [localValue, setLocalValue] = useState(
    value || isMultiple ? value : "",
  );
  const [open, setOpen] = useState(false);
  const { isMobile } = useMedia();
  const translations = useTranslations();
  const inputName = `autocomplete_${elementName || "input"}`;

  const hasError = hasErrorOverride || isValid(validation) === false;

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    const valueFromEvent = event.target.value;
    if (valueFromEvent.length >= minCharacterForRefinement) {
      refine(valueFromEvent);
      setOpen(true);
    }
  };

  const debouncedHandleSearch = useMemo(() => debounce(handleSearch, 100), []);

  const paste = usePaste(onChange);

  useEffect(() => {
    if (value == null) {
      setLocalValue(isMultiple ? [] : "");
    } else {
      setLocalValue(value);
    }
  }, [value]);

  if (localValue == null) return null;

  const isMaxOptions =
    isMultiple &&
    !!maxOptions &&
    (localValue as SelectOption[]).length >= maxOptions;

  return (
    <>
      {withPaste && pasteContext && (
        <RSButton
          id="paste"
          LeftIcon={ListPlusIcon}
          loading="na"
          style={{ margin: margin(0, -1) }}
          tooltip={pasteContext?.tooltipText}
          variant="text"
          onClick={paste}
        >
          {pasteContext.buttonText}
        </RSButton>
      )}
      <Autocomplete
        autoHighlight={enableAutoHighlight}
        clearText={translations.actions.clear}
        closeText={translations.actions.close}
        componentsProps={{
          clearIndicator: { "aria-hidden": "true" },
          popper: {
            modifiers: [
              {
                enabled: false,
                name: "flip",
              },
            ],
            placement: "bottom",
            sx: { zIndex: zIndex || Z_INDEX_DRAWER_CONTENT },
          },
        }}
        disableCloseOnSelect={!shouldCloseMenuOnSelect}
        disabled={isDisabled}
        disablePortal={!withPortal}
        filterOptions={(option) => option} // DEV-11459: to avoid filtering the algolia result
        filterSelectedOptions={shouldFilterSelectedOptions}
        id={inputName}
        isOptionEqualToValue={(option, selectedOption) => {
          return option.value === selectedOption.value;
        }}
        ListboxProps={ListboxProps}
        multiple={isMultiple}
        noOptionsText={noOptionsText}
        open={
          !!maxOptions && isMultiple
            ? open && (localValue as SelectOption[]).length < maxOptions
            : open
        }
        openText={translations.actions.expand}
        options={hits.map(renderHits)}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={isMaxOptions}
            error={hideError ? false : hasError}
            FormHelperTextProps={
              {
                "data-testid": `${inputName}-form-helper-text`,
              } as FormHelperTextProps
            }
            helperText={
              hideError
                ? undefined
                : getHelperText({
                    hasCustomValidation: !!hasCustomValidation,
                    translations,
                    validation,
                  })
            }
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: shouldDisableLabelShrink ? false : undefined,
              style: { fontSize: isMobile ? FONT_SIZE_16 : FONT_SIZE_14 },
            }}
            inputProps={{
              ...params.inputProps,
              "aria-label": ariaLabel,
              "aria-labelledby": ariaLabelledBy,
              "data-testid": inputName,
              id: id ?? params.inputProps.id,
            }}
            InputProps={{
              ...params.InputProps,
              disableUnderline: shouldDisableUnderline,
              style: {
                fontSize: isMobile ? FONT_SIZE_16 : FONT_SIZE_14,
              },
            }}
            label={label}
            name={elementName}
            placeholder={placeholder}
            required={isRequired}
            sx={{
              "& input::placeholder": {
                opacity: 0.6,
              },
            }}
            value={currentRefinement}
            variant="standard"
            onChange={debouncedHandleSearch}
          />
        )}
        renderOption={({ onMouseOver: _, ...props }, option, state) => {
          return (
            <MenuItem
              {...props}
              key={getKey(props.id)}
              data-testid={`option-${option.value}`}
              divider
              selected={state.selected}
              sx={{
                fontSize: FONT_SIZE_14,
                whiteSpace: shouldTruncateMenuItems ? undefined : "normal",
              }}
            >
              <div
                style={{
                  ...(shouldTruncateMenuItems
                    ? { overflow: "hidden", textOverflow: "ellipsis" }
                    : {}),
                }}
              >
                {option.label}
              </div>
            </MenuItem>
          );
        }}
        renderTags={(values, getTagProps: AutocompleteRenderGetTagProps) =>
          values.map((tagOption, index: number) => {
            const { key, ...rest } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...rest}
                data-testid={`selected-${tagOption.value}`}
                label={withShortValue ? tagOption.value : tagOption.label}
              />
            );
          })
        }
        size="small"
        sx={{
          fontSize: FONT_SIZE_14,
        }}
        value={localValue}
        onChange={(_, newValue) => {
          if (isMultiple && !!maxOptions) {
            if (newValue.length <= maxOptions) onChange(newValue);
          } else onChange(newValue);
        }}
        onClose={() => setOpen(false)}
        onKeyPress={(e) => {
          if (isMaxOptions) e.preventDefault();
        }}
        onOpen={() => setOpen(true)}
      />
    </>
  );
};

const AlgoliaSelect = connectAutoComplete<
  AlgoliaSelectPresenterProps & AutocompleteProvided
>(AlgoliaSelectPresenter);

export default function AlgoliaSelectDefault({
  algoliaAnalyticsName,
  defaultQuery,
  filters,
  hitsPerPage,
  indexName,
  isConnected,
  onChange,
  shouldSearchOnInitialRender,
  sideMutation,
  withIndexEnv,
  ...props
}: Omit<
  AlgoliaSelectPresenterProps,
  "currentRefinement" | "hits" | "onChange" | "refine"
> & {
  algoliaAnalyticsName: AlgoliaAnalyticsName;
  defaultQuery?: string;
  elementName: string;
  filters?: string;
  hideError?: boolean;
  hitsPerPage?: number;
  indexName: string;
  isConnected?: boolean;
  onChange?: AlgoliaSelectPresenterProps["onChange"];
  shouldSearchOnInitialRender?: boolean;
  sideMutation?: (newValue: any, mutateElement: any) => void;
  withIndexEnv?: boolean;
}) {
  const computedDefaultQuery =
    defaultQuery ??
    (shouldSearchOnInitialRender ? EMPTY_ALGOLIA_QUERY : DEFAULT_ALGOLIA_QUERY);
  const { analyticsTags, indexNameWithEnv, searchClient } =
    useAlgoliaSearchClient({
      algoliaAnalyticsName,
      indexName,
      indexWithEnv: withIndexEnv,
    });

  return (
    <div>
      <InstantSearch indexName={indexNameWithEnv} searchClient={searchClient}>
        <Configure
          analyticsTags={analyticsTags}
          clickAnalytics
          filters={filters}
          hitsPerPage={hitsPerPage ?? 50}
        />
        {isConnected ? (
          <AlgoliaSelectConnectedWithForm
            {...props}
            defaultRefinement={computedDefaultQuery}
            sideMutation={sideMutation}
          />
        ) : (
          <AlgoliaSelect
            {...props}
            defaultRefinement={computedDefaultQuery}
            onChange={onChange || console.log}
          />
        )}
      </InstantSearch>
    </div>
  );
}
